/**
 * ChannelSwitcher — top-right control showing the active channel and a
 * dropdown to switch between registered channels or jump to the
 * onboarding wizard.
 */
import { Check, ChevronDown, Pencil, Plus } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { activateChannelApi, listChannelsApi, type Channel } from "../api";
import { hasDraft } from "../lib/brandSetup/draft";
import { useIsMobile } from "../lib/useIsMobile";

interface Props {
  onNavigateOnboard: () => void;
  /** Re-open the onboarding wizard on an existing channel to edit it. */
  onEditChannel: (slug: string) => void;
  /** Bumped by parent to force a refresh after creating a channel. */
  refreshKey?: number;
}

export function ChannelSwitcher({ onNavigateOnboard, onEditChannel, refreshKey = 0 }: Props): React.JSX.Element {
  const isMobile = useIsMobile();
  const [channels, setChannels] = useState<Channel[]>([]);
  const [activeSlug, setActiveSlug] = useState<string>("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  // "Continue setup" vs "Set up a brand" (spec §2.6) — re-checked on mount
  // and every time the dropdown opens, since a step walked earlier in this
  // same session writes the draft without remounting this component.
  const [draftExists, setDraftExists] = useState(false);
  useEffect(() => {
    try {
      setDraftExists(hasDraft(localStorage) !== null);
    } catch {
      setDraftExists(false);
    }
  }, [open]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const r = await listChannelsApi();
        if (cancelled) return;
        setChannels(r.channels);
        setActiveSlug(r.activeSlug);
      } catch {
        // Server down or channel system not yet bootstrapped — render
        // a graceful empty state rather than crash the app shell.
      }
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent): void => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  const active = channels.find((c) => c.slug === activeSlug);

  async function switchTo(slug: string): Promise<void> {
    setOpen(false);
    if (slug === activeSlug) return;
    try {
      await activateChannelApi(slug);
      setActiveSlug(slug);
      // Hard reload — the active channel changes server-side state that
      // affects almost every page (settings, library, scheduling). Reloading
      // is the simplest correct way to flush every cache.
      window.location.reload();
    } catch (err) {
      console.warn("activate failed", err);
    }
  }

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <button
        data-tour="brand-chip"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "6px 12px",
          borderRadius: 12,
          background: "rgba(255,255,255,.04)",
          border: "1px solid rgba(255,255,255,.08)",
          color: "var(--ql-fg)",
          cursor: "pointer",
          fontFamily: "inherit",
          fontSize: 13,
        }}
      >
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            background: active?.brand.accent ?? "#666",
            boxShadow: active ? `0 0 8px ${active.brand.accent}` : "none",
          }}
        />
        <span>{active?.displayName ?? "No channel"}</span>
        <ChevronDown size={14} style={{ opacity: 0.6 }} />
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            minWidth: 240,
            maxWidth: isMobile ? "calc(100vw - 24px)" : undefined,
            background: "rgba(16,18,27,.96)",
            border: "1px solid rgba(255,255,255,.08)",
            borderRadius: 14,
            padding: 6,
            backdropFilter: "blur(12px)",
            zIndex: 50,
            boxShadow: "0 12px 32px rgba(0,0,0,.4)",
          }}
        >
          {channels.map((c) => (
            <div
              key={c.slug}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                borderRadius: 10,
                background: c.slug === activeSlug ? "rgba(255,213,0,.06)" : "transparent",
              }}
            >
              <button
                onClick={() => void switchTo(c.slug)}
                title={c.slug === activeSlug ? "Active channel" : `Switch to ${c.displayName}`}
                style={{
                  display: "flex", flex: 1, minWidth: 0, alignItems: "center", gap: 10,
                  padding: "10px 12px", background: "transparent", border: "none", borderRadius: 10,
                  color: "var(--ql-fg)", cursor: "pointer", fontFamily: "inherit", fontSize: 13, textAlign: "left",
                }}
              >
                <span style={{ width: 10, height: 10, borderRadius: "50%", background: c.brand.accent, flex: "none" }} />
                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.displayName}</span>
                {c.slug === activeSlug && <Check size={14} style={{ opacity: 0.7, flex: "none" }} />}
              </button>
              <button
                onClick={() => { setOpen(false); onEditChannel(c.slug); }}
                title={`Edit ${c.displayName} — colors, layout, captions, font`}
                aria-label={`Edit ${c.displayName}`}
                style={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "none",
                  width: 32, height: 32, marginRight: 6, borderRadius: 8, background: "transparent",
                  border: "1px solid transparent", color: "var(--ql-fg-subtle)", cursor: "pointer",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,.06)"; e.currentTarget.style.color = "var(--ql-fg)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--ql-fg-subtle)"; }}
              >
                <Pencil size={13} strokeWidth={2} />
              </button>
            </div>
          ))}
          <div style={{ height: 1, background: "rgba(255,255,255,.05)", margin: "6px 4px" }} />
          <button
            onClick={() => { setOpen(false); onNavigateOnboard(); }}
            style={{
              display: "flex",
              width: "100%",
              alignItems: "center",
              gap: 10,
              padding: "10px 12px",
              background: "transparent",
              border: "none",
              borderRadius: 10,
              color: "var(--ql-fg-muted)",
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: 13,
              textAlign: "left",
            }}
          >
            <Plus size={14} />
            <span>{draftExists ? "Continue setup" : "Set up a brand"}</span>
          </button>
        </div>
      )}
    </div>
  );
}
